<template>
  <div class="exam-question">
    <div class="question-header">
      <h4>Question {{ questionNumber }}</h4>
      <span v-if="question.points" class="points">({{ question.points }} point{{ question.points !== 1 ? 's' : '' }})</span>
    </div>
    
    <div class="question-text rich-text" v-html="question.content"></div>
    
    <div class="answer-options mt-3">
      <!-- Multiple Choice Questions -->
      <div v-if="question.type === 'Multiple Choice'" class="multiple-choice">
        <div 
          v-for="choice in question.answerChoices" 
          :key="choice.id"
          class="form-check mb-2"
        >
          <input
            :id="`choice-${choice.id}`"
            :value="choice.id"
            v-model="selectedAnswer"
            type="radio"
            class="form-check-input"
            @change="onAnswerChange"
          />
          <label :for="`choice-${choice.id}`" class="form-check-label">
            {{ choice.content }}
          </label>
        </div>
      </div>

      <!-- Multiple Answer Questions -->
      <div v-else-if="question.type === 'Multiple Answer'" class="multiple-select">
        <div 
          v-for="choice in question.answerChoices" 
          :key="choice.id"
          class="form-check mb-2"
        >
          <input
            :id="`choice-${choice.id}`"
            :value="choice.id"
            v-model="selectedAnswers"
            type="checkbox"
            class="form-check-input"
            @change="onAnswerChange"
          />
          <label :for="`choice-${choice.id}`" class="form-check-label">
            {{ choice.content }}
          </label>
        </div>
      </div>

      <!-- Text Answer Questions -->
      <div v-else-if="question.type === 'Text'" class="text-answer">
        <textarea
          v-model="textAnswer"
          class="form-control w-full bg-white"
          rows="6"
          placeholder="Enter your answer here..."
          @input="onAnswerChange"
        ></textarea>
      </div>

      <!-- Drop Down Questions -->
      <div v-else-if="question.type === 'Drop Down'" class="dropdown-answer">
        <select v-model="selectedAnswer" class="form-control w-full bg-white border border-gray-300 rounded-md px-3 py-2" @change="onAnswerChange">
          <option value="">Select an answer</option>
          <option v-for="choice in question.answerChoices" :key="choice.id" :value="choice.id">
            {{ choice.content }}
          </option>
        </select>
      </div>

      <!-- True/False Questions -->
      <div v-else-if="question.type === 'True/False'" class="true-false">
        <div 
          v-for="choice in question.answerChoices" 
          :key="choice.id"
          class="form-check mb-2"
        >
          <input
            :id="`choice-${choice.id}`"
            :value="choice.id"
            v-model="selectedAnswer"
            type="radio"
            class="form-check-input"
            @change="onAnswerChange"
          />
          <label :for="`choice-${choice.id}`" class="form-check-label">
            {{ choice.content }}
          </label>
        </div>
      </div>

      <!-- File Upload Questions -->
      <div v-else-if="question.type === 'File Upload'" class="file-upload">
        <div class="file-upload-container border-2 border-dashed border-gray-300 rounded-lg p-6 bg-gray-50 hover:bg-gray-100 transition-colors">
          <input
            type="file"
            class="form-control w-full bg-white border border-gray-300 rounded-md px-3 py-2"
            multiple
            @change="onFileChange"
          />
          <div class="mt-3 text-center">
            <svg class="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
              <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
            <p class="mt-2 text-sm text-gray-800">
              <span class="font-medium text-blue-600 hover:text-blue-500">Click to upload files</span>
              or drag and drop
            </p>
            <p class="text-xs text-gray-800">
              You can upload multiple files for this question (max 20MB each)
            </p>
          </div>
        </div>
      </div>

      <!-- Default fallback for unknown question types -->
      <div v-else class="unknown-type">
        <p class="text-muted">Unknown question type: {{ question.type }}</p>
      </div>
    </div>

    <!-- Read Aloud Feature -->
    <div class="read-aloud-section mt-3">
      <button 
        v-if="!isReading" 
        @click="readQuestionAloud" 
        class="text-blue-600 hover:text-blue-800 underline text-sm"
      >
        Read Aloud
      </button>
      <button 
        v-else 
        @click="stopReading" 
        class="text-red-600 hover:text-red-800 underline text-sm"
      >
        Stop Reading
      </button>
    </div>
  </div>
</template>

<script>
import { ref, watch, onMounted } from 'vue';

export default {
  name: 'ExamQuestion',
  props: {
    question: {
      type: Object,
      required: true
    },
    questionNumber: {
      type: Number,
      required: true
    },
    initialAnswer: {
      type: Object,
      default: null
    }
  },
  emits: ['answer-selected'],
  setup(props, { emit }) {
    const selectedAnswer = ref('');
    const selectedAnswers = ref([]);
    const textAnswer = ref('');
    const uploadedFiles = ref([]);
    const isReading = ref(false);

    // Initialize with saved answer if available
    const initializeAnswer = () => {
      if (props.initialAnswer) {
        switch (props.initialAnswer.type) {
          case 'choice':
            selectedAnswer.value = props.initialAnswer.value;
            break;
          case 'multiple':
            selectedAnswers.value = props.initialAnswer.value || [];
            break;
          case 'text':
            textAnswer.value = props.initialAnswer.value || '';
            break;
          case 'file':
            // Can't restore actual file objects, but we can show that an answer was provided
            uploadedFiles.value = [];
            break;
        }
      }
    };

    // Initialize on mount
    onMounted(() => {
      initializeAnswer();
    });

    // Watch for changes in initialAnswer prop
    watch(() => props.initialAnswer, () => {
      initializeAnswer();
    }, { immediate: true });

    const onAnswerChange = () => {
      let answer = null;

      switch (props.question.type) {
        case 'Multiple Choice':
        case 'True/False':
          answer = {
            type: 'choice',
            value: selectedAnswer.value,
            questionId: props.question.id
          };
          break;
        case 'Multiple Answer':
          answer = {
            type: 'multiple',
            value: selectedAnswers.value,
            questionId: props.question.id
          };
          break;
        case 'Text':
          answer = {
            type: 'text',
            value: textAnswer.value,
            questionId: props.question.id
          };
          break;
        case 'Drop Down':
          answer = {
            type: 'choice',
            value: selectedAnswer.value,
            questionId: props.question.id
          };
          break;
        case 'File Upload':
          answer = {
            type: 'file',
            value: uploadedFiles.value,
            questionId: props.question.id
          };
          break;
      }

      if (answer) {
        emit('answer-selected', answer);
      }
    };

    const onFileChange = (event) => {
      uploadedFiles.value = Array.from(event.target.files);
      onAnswerChange();
    };

    // Watch for changes in the question to reset answers
    watch(() => props.question.id, () => {
      selectedAnswer.value = '';
      selectedAnswers.value = [];
      textAnswer.value = '';
      uploadedFiles.value = [];
    });

    // Read aloud functionality
    const readQuestionAloud = () => {
      if ('speechSynthesis' in window) {
        // Stop any current speech
        window.speechSynthesis.cancel();
        
        // Create text to read
        let textToRead = `Question ${props.questionNumber}. ${props.question.content}`;
        
        // Add answer choices for multiple choice questions
        if (props.question.answerChoices && props.question.answerChoices.length > 0) {
          textToRead += '. The options are: ';
          const options = props.question.answerChoices.map((choice, index) => {
            return `Option ${String.fromCharCode(65 + index)}: ${choice.content}`;
          }).join('. ');
          textToRead += options;
        }
        
        // Create speech synthesis utterance
        const utterance = new SpeechSynthesisUtterance(textToRead);
        
        // Set up event listeners
        utterance.onstart = () => {
          isReading.value = true;
        };
        
        utterance.onend = () => {
          isReading.value = false;
        };
        
        utterance.onerror = () => {
          isReading.value = false;
        };
        
        // Start speaking
        window.speechSynthesis.speak(utterance);
      } else {
        alert('Sorry, your browser does not support text-to-speech functionality.');
      }
    };

    const stopReading = () => {
      if ('speechSynthesis' in window) {
        window.speechSynthesis.cancel();
        isReading.value = false;
      }
    };

    return {
      selectedAnswer,
      selectedAnswers,
      textAnswer,
      uploadedFiles,
      isReading,
      onAnswerChange,
      onFileChange,
      readQuestionAloud,
      stopReading
    };
  }
};
</script>

<style scoped>
.exam-question {
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 20px;
}

.question-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.question-header h4 {
  margin: 0;
  color: #333;
}

.points {
  color: #666;
  font-size: 14px;
  font-weight: normal;
}

.question-text {
  margin-bottom: 20px;
  line-height: 1.6;
}

.form-check {
  margin-bottom: 10px;
}

.form-check-label {
  margin-left: 8px;
  cursor: pointer;
}

.form-control {
  border-radius: 4px;
}

.unknown-type {
  padding: 10px;
  background: #fff3cd;
  border: 1px solid #ffeaa7;
  border-radius: 4px;
}
</style>